import { useEffect, useRef, useState } from "react";
import { FormProvider as RHFormProvider, useForm } from "react-hook-form";
import { FormProvider, useFormContext } from "./RegistrationFormContext";
import StepIndicator from "./StepIndicator";
import Step1Contacts from "./Step1Contacts";
import Step2Pupils from "./Step2Pupils";
import Step3Attachments from "./Step3Attachments";
import Step4PrevSchools from "./Step4PrevSchools";
import Step5Review from "./Step5Review";
import { toast } from "react-toastify";
import { get, post } from "../../services/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import ConfirmationModal from "./ConfirmationModal";
import DropdownWithCustomValuesModal from "../../components/DropdownWithCustomValuesModal";
import Step6Siblings from "./Step6Siblings";
import NotificationEventTypes from "../../constants/notificationEventsEnum";
import { useWorkflow } from "../../context/WorkflowContext";
import WorkflowProcessIds from "../../constants/WorkflowProcessIdsEnum";
import DBObjectIds from "../../constants/DBObjectsIdsEnum";

const RegistrationForm = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const roleId = localStorage.getItem("roleId");
  const loggedInUserId = localStorage.getItem("userId");
  const { sharedData, setSharedData } = useFormContext();
  const { workflowParams } = useWorkflow();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get("userId");
  const isEditMode = Boolean(userIdFromUrl);
  const [
    isCustomDropdownValuesModalVisible,
    setIsCustomDropdownValuesModalVisible,
  ] = useState(false);
  const stepRefs = useRef([]);
  const [currentStep, setCurrentStep] = useState(1);
  const [openAccordions, setOpenAccordions] = useState([1]);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const userIdToUse = userIdFromUrl
      ? Number(userIdFromUrl)
      : Number(loggedInUserId);
    if (userIdToUse) {
      setSharedData((prevData) => ({
        ...prevData,
        userId: userIdToUse,
        userIdFromUrl: userIdFromUrl ? Number(userIdFromUrl) : null,
        isEditMode: isEditMode,
      }));
    } else {
      console.warn(
        "No valid userId available, using loggedInUserId as fallback:",
        loggedInUserId
      );
    }
  }, [userIdFromUrl, isEditMode, loggedInUserId, setSharedData]);

  const scrollToStepHeader = (stepNumber) => {
    setTimeout(() => {
      const headerElement = document.getElementById(`heading${stepNumber}`);
      const scrollContainer = scrollContainerRef.current;
      if (headerElement && scrollContainer) {
        const headerTop = headerElement.getBoundingClientRect().top;
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const offset =
          headerTop - containerTop + scrollContainer.scrollTop - 10;
        scrollContainer.scrollTo({
          top: offset,
          behavior: "smooth",
        });
      }
    }, 300); // Increased delay to 300ms for Bootstrap animation
  };

  useEffect(() => {
    setOpenAccordions((prev) => {
      if (!prev.includes(currentStep)) {
        return [...prev, currentStep];
      }
      return prev;
    });
    scrollToStepHeader(currentStep);
  }, [currentStep]);

  const methods = useForm({});

  const onSubmit = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    // if (currentStep !== steps.length) {
    //   return;
    // }

    try {
      const response = await get(
        `Student/GetPupilsInformationByUserId?userId=${sharedData.userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const hasUnsubmittedForms = response.some(
        (item) => item.isSubmit === false
      );
      if (hasUnsubmittedForms) {
        setShowConfirmationModal(true);
      } else {
        await handleConfirmSubmission(hasUnsubmittedForms);
      }
    } catch (error) {
      console.error("Error fetching pupil information:", error);
    }
  };

  const handleConfirmSubmission = async (hasUnsubmittedForms) => {
    setShowConfirmationModal(false);
    setIsLoading(true);

    const roleId = localStorage.getItem("roleId");
    const userIdFromStorage = localStorage.getItem("userId");
    const userIdFromSharedData = sharedData.userId;

    const payload = {
      roleId: roleId,
      code: sharedData.code,
      updatedBy: String(userIdFromSharedData || userIdFromStorage),
      NotificationEventTypeIds: hasUnsubmittedForms
        ? NotificationEventTypes.ADD.toString()
        : NotificationEventTypes.UPDATE.toString(),
      FormId: 71,
      Workflow: {
        ...workflowParams.Workflow,
        WorkflowStepId: workflowParams.Workflow.WorkflowStepId,
        CreatedBy: Number(userIdFromStorage),
      },
    };

    try {
      const response = await post(
        "Student/UpdateFirstRegistrationSubmissionDate",
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.isSuccess) {
        toast.success("The form has been submitted successfully!");
        let userIdToUse = userIdFromSharedData || userIdFromStorage;
        if ((roleId === "3" || roleId === "2") && userIdToUse) {
          navigate(`/medical-form?userId=${userIdToUse}`);
        } else {
          navigate(`/medical-form`);
        }
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error.", error);
    } finally {
      setIsLoading(false);
    }
  };

  const moveToNextStep = async (stepNumber = currentStep + 1) => {
    // Validate current step before navigating
    const currentStepIndex = currentStep - 1;
    if (
      stepRefs.current[currentStepIndex] &&
      typeof stepRefs.current[currentStepIndex].canProceedToNextStep ===
        "function"
    ) {
      const canProceed = await stepRefs.current[
        currentStepIndex
      ].canProceedToNextStep();
      if (!canProceed) {
        if (currentStep === 1) {
          const contactData =
            stepRefs.current[0].getValues?.("studentFamilyInformationDtos") ||
            [];
          if (contactData.length < 2) {
            toast.error("Please add at least 2 contacts before proceeding.");
          } else if (!contactData.some((contact) => contact.isPrimary)) {
            toast.error(
              "Please ensure at least one contact is marked as primary."
            );
          }
        } else if (currentStep === 2) {
          toast.error("Please add at least one pupil before proceeding.");
        } else {
          toast.error(
            `Please complete the ${steps[currentStepIndex].title} step before proceeding.`
          );
        }
        return;
      }
    }

    // Validate Pupils step if navigating to Documents or beyond
    if (stepNumber > 2 && currentStep <= 2) {
      try {
        const response = await get(
          `Student/GetPupilsInformationByUserId?userId=${sharedData.userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response || response.length < 1) {
          toast.error("Please add at least one pupil before proceeding.");
          return;
        }
      } catch (error) {
        console.error("Error fetching pupils:", error);
        toast.error("Error checking pupils. Please try again.");
        return;
      }
    }

    if (stepNumber >= 1 && stepNumber <= steps.length) {
      setCurrentStep(stepNumber);
      setOpenAccordions((prev) => {
        if (!prev.includes(stepNumber)) {
          return [...prev, stepNumber];
        }
        return prev;
      });
      scrollToStepHeader(stepNumber);
    }
  };

  const steps = [
    {
      title: "Contacts",
      component: (
        <Step1Contacts
          ref={(el) => (stepRefs.current[0] = el)}
          isOpen={openAccordions.includes(1)}
          moveToNextStep={moveToNextStep}
        />
      ),
    },
    {
      title: "Pupils",
      component: (
        <Step2Pupils
          ref={(el) => (stepRefs.current[1] = el)}
          isOpen={openAccordions.includes(2)}
          moveToNextStep={moveToNextStep}
        />
      ),
    },
    {
      title: "Documents",
      component: (
        <Step3Attachments
          ref={(el) => (stepRefs.current[2] = el)}
          isOpen={openAccordions.includes(3)}
          currentStep={currentStep}
          moveToNextStep={moveToNextStep}
        />
      ),
    },
    {
      title: "Previous Schools",
      component: (
        <Step4PrevSchools
          ref={(el) => (stepRefs.current[3] = el)}
          isOpen={openAccordions.includes(4)}
          currentStep={currentStep}
          moveToNextStep={moveToNextStep}
        />
      ),
    },
    {
      title: "Siblings",
      component: (
        <Step6Siblings
          ref={(el) => (stepRefs.current[4] = el)}
          isOpen={openAccordions.includes(5)}
          currentStep={currentStep}
          moveToNextStep={moveToNextStep}
        />
      ),
    },
    {
      title: "Review",
      component: (
        <Step5Review
          ref={(el) => (stepRefs.current[5] = el)}
          isOpen={openAccordions.includes(6)}
          currentStep={currentStep}
          onSubmit={methods.handleSubmit(onSubmit)}
          isSubmitting={methods.formState.isSubmitting}
          isLoading={isLoading}
          // moveToNextStep={moveToNextStep}
        />
      ),
    },
  ];

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  const handleToggleAccordion = debounce(async (stepNumber) => {
    toast.dismiss();

    // If navigating to Step 2 or beyond, check Step 1 and Pupils requirements
    if (stepNumber >= 2) {
      if (
        stepRefs.current[0] &&
        typeof stepRefs.current[0].canProceedToNextStep === "function"
      ) {
        const canProceed = await stepRefs.current[0].canProceedToNextStep();
        if (!canProceed) {
          const contactData =
            stepRefs.current[0].getValues?.("studentFamilyInformationDtos") ||
            [];
          if (contactData.length < 2) {
            toast.error("Please add at least 2 contacts before proceeding.");
            return;
          } else if (!contactData.some((contact) => contact.isPrimary)) {
            toast.error(
              "Please ensure at least one contact is marked as primary."
            );
            return;
          }
        }
      }
    }

    // Validate Pupils step if navigating to Documents or beyond
    if (stepNumber > 2) {
      try {
        const response = await get(
          `Student/GetPupilsInformationByUserId?userId=${sharedData.userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response || response.length < 1) {
          toast.error("Please add at least one pupil before proceeding.");
          return;
        }
      } catch (error) {
        console.error("Error fetching pupils:", error);
        toast.error("Error checking pupils. Please try again.");
        return;
      }
    }

    setOpenAccordions((prev) => {
      if (prev.includes(stepNumber)) {
        return prev.filter((step) => step !== stepNumber);
      } else {
        const newAccordions = [...prev, stepNumber];
        scrollToStepHeader(stepNumber);
        return newAccordions;
      }
    });
    setCurrentStep(stepNumber);
  }, 300);

  const handleNext = async (event) => {
    event.preventDefault();
    const isValid = await methods.trigger();
    if (!isValid) return;

    // const currentStepRef = stepRefs.current[currentStep - 1];
    // if (currentStepRef && typeof currentStepRef.save === "function") {
    //   setIsLoading(true);
    //   const saveSuccessful = await currentStepRef.save();
    //   setIsLoading(false);
    //   if (!saveSuccessful) {
    //     return;
    //   }
    // }

    if (
      currentStep === 1 &&
      stepRefs.current[0] &&
      typeof stepRefs.current[0].canProceedToNextStep === "function"
    ) {
      const canProceed = await stepRefs.current[0].canProceedToNextStep();
      if (!canProceed) {
        return;
      }
    }

    if (
      currentStep === 2 &&
      stepRefs.current[1] &&
      typeof stepRefs.current[1].canProceedToNextStep === "function"
    ) {
      const canProceed = await stepRefs.current[1].canProceedToNextStep();
      if (!canProceed) {
        return;
      }
    }

    const newStep = Math.min(currentStep + 1, steps.length);
    setCurrentStep(newStep);
  };

  const handlePrev = () => {
    const newStep = Math.max(currentStep - 1, 1);
    setCurrentStep(newStep);
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Registration Form</h2>
      </div>

      <div className="scrollable-container">
        <div className="">
          <RHFormProvider {...methods}>
            <div
              className="accordion custom-accordion"
              id="registrationAccordion"
              ref={scrollContainerRef}
              style={{ height: "calc(100vh - 156px)", overflowY: "auto" }}
            >
              {steps.map((step, index) => {
                const stepNumber = index + 1;
                const isActive = currentStep === stepNumber;
                const isDone = currentStep > stepNumber;

                return (
                  <div className="accordion-item" key={stepNumber}>
                    <h2
                      className="accordion-header"
                      id={`heading${stepNumber}`}
                    >
                      <button
                        className={`accordion-button ${
                          isActive ? "active" : ""
                        } ${isDone ? "done" : ""}`}
                        type="button"
                        onClick={() => handleToggleAccordion(stepNumber)}
                        aria-expanded={openAccordions.includes(stepNumber)}
                        aria-controls={`collapse${stepNumber}`}
                      >
                        <span className="step-number">{stepNumber}</span>
                        {step.title}
                        <span className="step-number-with-total ms-auto">
                          Step {stepNumber} of {steps.length}
                        </span>
                      </button>
                    </h2>
                    <div
                      id={`collapse${stepNumber}`}
                      className={`accordion-collapse collapse ${
                        openAccordions.includes(stepNumber) ? "show" : ""
                      }`}
                      aria-labelledby={`heading${stepNumber}`}
                      data-bs-parent="#registrationAccordion"
                    >
                      <div className="accordion-body new-form-style">
                        {step.component}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* <form onSubmit={methods.handleSubmit(onSubmit)}>
              <div className="form-navigation mt-4">
                <button
                  type="button"
                  onClick={handlePrev}
                  className="btn btn-outline-primary me-2"
                  disabled={currentStep === 1}
                >
                  Previous
                </button>
                {currentStep < steps.length ? (
                  <>
                     <button
                      type="button"
                      onClick={handleNext}
                      className="btn btn-primary"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <span
                          className="spinner-border spinner-custom-size me-1"
                          aria-hidden="true"
                        ></span>
                      ) : null}
                      <span role="status">Go to {steps[currentStep].title}</span>
                    </button> 
                  </>
                ) : (
                  <>
                    <button
                    className="btn btn-primary ms-auto"
                    type="submit"
                    disabled={methods.formState.isSubmitting || isLoading}
                    >
                    {methods.formState.isSubmitting || isLoading ? (
                      <span
                      className="spinner-border spinner-custom-size me-1"
                      aria-hidden="true"
                      ></span>
                    ) : null}
                    <span role="status">Submit & Continue</span>
                  </button>
                  </>
                )}
              </div>
            </form> */}
          </RHFormProvider>
        </div>
      </div>

      {showConfirmationModal && (
        <ConfirmationModal
          onConfirm={handleConfirmSubmission}
          onCancel={() => setShowConfirmationModal(false)}
        />
      )}
    </>
  );
};

export default () => (
  <FormProvider>
    <RegistrationForm />
  </FormProvider>
);
